import type { AppStore } from '../core/store';
import type { AppState, MapTool, WorkingGeometryType } from '../core/types';
import { normalizeLayerOrder } from '../domain/layers';
import { emptySelectionSet } from '../domain/selectionSet';
import { createWorkingLayer } from '../domain/workingLayers';

const required = <T extends Element>(root: ParentNode, selector: string): T => {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`No se encontró ${selector}`);
  return element;
};

const compatibleTool = (type: WorkingGeometryType, tool: string): boolean =>
  (type === 'Point' && tool === 'draw-point')
  || (type === 'LineString' && tool === 'draw-line')
  || (type === 'Polygon' && (tool === 'draw-polygon' || tool === 'draw-rectangle'));

export class WorkingLayerPanel {
  constructor(private readonly element: HTMLElement, private readonly store: AppStore, private readonly activateTool: (tool: MapTool) => void) {
    required(element, '#create-working-layer').addEventListener('click', () => this.createLayer());
    element.querySelectorAll<HTMLButtonElement>('[data-draw-tool]').forEach((button) => button.addEventListener('click', () => this.activateTool(button.dataset.drawTool as MapTool)));
  }

  render(state: AppState): void {
    const active = state.layers.find((layer) => layer.id === state.activeLayerId && layer.kind === 'working');
    this.element.querySelectorAll<HTMLButtonElement>('[data-draw-tool]').forEach((button) => {
      button.disabled = !active || active.locked || !compatibleTool(active.workingGeometryType!, button.dataset.drawTool ?? '');
      button.classList.toggle('active', button.dataset.drawTool === state.activeTool);
    });
    const status = required(this.element, '#drawing-status');
    if (!active) status.textContent = 'Crea o activa una capa de trabajo.';
    else if (active.locked) status.textContent = `${active.name} está bloqueada.`;
    else if (!status.textContent?.startsWith('Dibuj')) status.textContent = `${active.name} · ${active.features.length} objeto(s) · EPSG:3857`;
  }

  private createLayer(): void {
    const nameInput = required<HTMLInputElement>(this.element, '#working-layer-name');
    const type = required<HTMLSelectElement>(this.element, '#working-layer-type').value as WorkingGeometryType;
    const layer = createWorkingLayer(nameInput.value, type);
    const layers = normalizeLayerOrder([...this.store.getState().layers, layer]);
    this.store.update({
      layers,
      activeLayerId: layer.id,
      activeTool: 'select',
      selectionSet: emptySelectionSet(),
      genericSelection: null,
      selection: null,
    });
    nameInput.value = '';
    this.element.setAttribute('open', '');
  }
}
